import * as validator from 'gltf-validator';
import { GenericFileResource } from './GenericFileResource';
import { FILE_SIZE_LIMIT } from '../constants';
import { hasDracoCompression, checkTextureSize } from 'gltf-optimizer-browser';

export enum ValidationErrorType {
    SIZE_EXCEEDED = 'SIZE_EXCEEDED',
    GLTF_VALIDATOR_ERRORS = 'GLTF_VALIDATOR_ISSUES',
    SOMETHING_WENT_WRONG = 'Something went wrong',
    HAS_DRACO_COMPRESSION = 'HAS_DRACO_COMPRESSION',
    TEXTURE_SIZE_EXCEEDED = 'TEXTURE_SIZE_EXCEEDED'
}

export interface ValidatorReport {
    issues: { messages: ValidatorMessage[] };
}

interface ValidatorMessage {
    severity: number;
}

export interface GLTFValidationReport {
    valid: boolean;
    errorType?: ValidationErrorType | string;
    validationReport?: ValidatorReport;
}
export interface FileValidationReport {
    valid: boolean;
    size?: number;
    name?: string;
    time?: Date;
    report?: GLTFValidationReport;
}

export class GLTFFileResource extends GenericFileResource {
    public validationReport?: GLTFValidationReport;

    public async validate(): Promise<void> {
        if (this.validationReport) return;
        if (this.size > FILE_SIZE_LIMIT) {
            this.validationReport = { valid: false, errorType: ValidationErrorType.SIZE_EXCEEDED };
            return;
        }
        const validatorResults: ValidatorReport = await validator.validateBytes(new Uint8Array(this.data), { ignoredIssues: [], maxIssues: 5 });
        this.validationReport = { valid: false, validationReport: validatorResults };
        const hasIssues: boolean = validatorResults.issues.messages.filter((f) => f.severity === 0).length !== 0;
        if (!hasIssues) {
            this.validationReport.valid = true;
            return;
        }
        this.validationReport.errorType = ValidationErrorType.GLTF_VALIDATOR_ERRORS;
    }
}

export const validateGlb = async (file: File): Promise<GLTFValidationReport> => {
    let report: GLTFValidationReport = { valid: false };
    if (file.size > FILE_SIZE_LIMIT) {
        report = { ...report, errorType: ValidationErrorType.SIZE_EXCEEDED };
        return Promise.resolve(report);
    }
    const arrayBuffer: ArrayBuffer = await file.arrayBuffer();
    const buffer = new Uint8Array(arrayBuffer);
    if (await hasDracoCompression(buffer)) {
        report = { ...report, errorType: ValidationErrorType.HAS_DRACO_COMPRESSION };
        return Promise.resolve(report);
    }

    if (await checkTextureSize(buffer)) {
        report = { ...report, errorType: ValidationErrorType.TEXTURE_SIZE_EXCEEDED };
        return Promise.resolve(report);
    }
    const validatorResults: ValidatorReport = await validator.validateBytes(new Uint8Array(await file.arrayBuffer()), { ignoredIssues: [], maxIssues: 5 });
    report = { ...report, validationReport: validatorResults };
    const hasIssues: boolean = validatorResults.issues.messages.filter((f) => f.severity === 0).length !== 0;
    if (!hasIssues) {
        report.valid = true;
        return Promise.resolve(report);
    }
    report.errorType = ValidationErrorType.GLTF_VALIDATOR_ERRORS;
    return Promise.resolve(report);
};
